import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { useToast } from '../../ui/use-toast';
import { useContext } from 'react';
import { RealTimeContext } from '../../../contexts/RealTimeContext';
import { 
  Users, 
  Play, 
  Square, 
  Coffee, 
  UserCheck,
  AlertTriangle,
  Package,
  Clock,
  MapPin,
  Briefcase,
  Loader2,
  Info,
  RefreshCw
} from 'lucide-react';
import { apiService, ApiUser, ApiWorkEntry } from '../../../services/api';
import { Project, Location, User } from '../../../types';
import { calculateWorkDuration, formatDuration, formatRomanianTime } from '../../../utils/timeUtils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Textarea } from '../../ui/textarea';
import { MaterialConsumptionForm } from '../../materials/MaterialConsumptionForm';

interface TeamMember extends ApiUser {
  isWorking: boolean;
  onBreak: boolean;
  currentWorkEntry?: ApiWorkEntry;
  activeBreakId?: number;
  todayHours: number;
  isSelected?: boolean;
}

interface TeamManagementTabProps {
  supervisorId: number;
  supervisorUser: User;
  projects: Project[];
  locations: Location[];
  teamId?: number;
  teamName?: string;
}

type RecentOperation = {
  operation: 'start' | 'stop';
  timestamp: number;
  projectId?: number;
  locationId?: number;
};

// Global storage for recent operations that persists across tab switches
const globalRecentOperations = new Map<string, RecentOperation>();

// Function to get a unique key for the team-user combination
const getOperationKey = (teamId: number | undefined, userId: number): string => {
  return `${teamId || 'all'}-${userId}`;
};

// Function to clean up old operations from global storage
const cleanupGlobalOperations = () => {
  const now = Date.now();
  for (const [key, op] of globalRecentOperations.entries()) {
    const timeout = op.operation === 'start' ? 35000 : 15000; // 35s for start, 15s for stop
    if (now - op.timestamp > timeout) {
      globalRecentOperations.delete(key);
    }
  }
};

export const TeamManagementTab: React.FC<TeamManagementTabProps> = ({
  supervisorId,
  supervisorUser,
  projects,
  locations,
  teamId,
  teamName
}) => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [selectAll, setSelectAll] = useState(false);
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [stopComments, setStopComments] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [selectedForStop, setSelectedForStop] = useState<TeamMember[]>([]);
  const [showMaterialDialog, setShowMaterialDialog] = useState(false);
  const [selectedForMaterial, setSelectedForMaterial] = useState<TeamMember | null>(null);
  const [bulkOperationProgress, setBulkOperationProgress] = useState<{
    isProcessing: boolean;
    currentMember: string | null;
    processedCount: number;
    totalCount: number;
    results: { memberId: number; memberName: string; success: boolean; error?: string }[];
  } | null>(null);
  const [processingMemberIds, setProcessingMemberIds] = useState<Set<number>>(new Set());
  const [recentlyUpdatedMemberIds, setRecentlyUpdatedMemberIds] = useState<Set<number>>(new Set());
  const [lastUpdateTimestamp, setLastUpdateTimestamp] = useState<number>(0);
  const loadTeamDataTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [recentOperations, setRecentOperations] = useState<Map<number, RecentOperation>>(new Map());
  const lastLoadCallRef = useRef<number>(0);
  const recentOperationsRef = useRef(recentOperations);
  const lastUpdateTimestampRef = useRef(lastUpdateTimestamp);
  const { toast } = useToast();
  
  // Initialize recent operations from global storage and sync with state
  useEffect(() => {
    // Clean up old operations first
    cleanupGlobalOperations();
    
    // Load operations for this team from global storage
    const teamOperations = new Map<number, RecentOperation>();
    for (const [key, op] of globalRecentOperations.entries()) {
      if (key.startsWith(`${teamId || 'all'}-`)) {
        const userId = parseInt(key.split('-')[1]);
        if (!isNaN(userId)) {
          teamOperations.set(userId, op);
        }
      }
    }
    
    if (teamOperations.size > 0) {
      console.log(`Restored ${teamOperations.size} recent operations from global storage for team ${teamId}`);
      setRecentOperations(teamOperations);
    }
  }, [teamId]);
  
  // Keep refs in sync with state and global storage
  useEffect(() => {
    recentOperationsRef.current = recentOperations;
    
    // Update global storage
    for (const [userId, op] of recentOperations.entries()) {
      const key = getOperationKey(teamId, userId);
      globalRecentOperations.set(key, op);
    }
    
    // Clean up operations that were removed
    for (const [key, op] of globalRecentOperations.entries()) {
      if (key.startsWith(`${teamId || 'all'}-`)) {
        const userId = parseInt(key.split('-')[1]);
        if (!isNaN(userId) && !recentOperations.has(userId)) {
          globalRecentOperations.delete(key);
        }
      }
    }
  }, [recentOperations, teamId]);
  
  useEffect(() => {
    lastUpdateTimestampRef.current = lastUpdateTimestamp;
  }, [lastUpdateTimestamp]);
  
  // Use context safely without throwing
  const realTimeContext = useContext(RealTimeContext);
  const subscribeToTeamMemberStatusChanged = realTimeContext?.subscribeToTeamMemberStatusChanged || (() => () => {});
  const subscribeToWorkStarted = realTimeContext?.subscribeToWorkStarted || (() => () => {});
  const subscribeToWorkEnded = realTimeContext?.subscribeToWorkEnded || (() => () => {});
  const subscribeToUserEvents = realTimeContext?.subscribeToUserEvents || (() => {});

  // Define loadTeamData function with debouncing
  const loadTeamData = useCallback(async (forceRefresh: boolean = false, ignoreRecentOperations: boolean = false) => {
    const now = Date.now();
    const timeSinceLastCall = now - lastLoadCallRef.current;
    
    // Detect rapid repeated calls (potential auto-refresh)
    if (!forceRefresh && timeSinceLastCall < 500) {
      console.log('Blocking rapid refresh call - possible auto-refresh detected', timeSinceLastCall + 'ms since last call');
      return;
    }
    
    // Check if we have recent bulk operations that shouldn't be overwritten
    if (!ignoreRecentOperations && !forceRefresh) {
      const timeSinceLastUpdate = Date.now() - lastUpdateTimestamp;
      if (timeSinceLastUpdate < 15000) { // 15 seconds protection window for optimized operations
        console.log('Blocking refresh - recent bulk operation detected', timeSinceLastUpdate + 'ms ago');
        return;
      }
    }
    
    lastLoadCallRef.current = now;
    console.log('Loading team data...', { forceRefresh, timeSinceLastCall });
    
    // Clear any existing timeout
    if (loadTeamDataTimeoutRef.current) {
      clearTimeout(loadTeamDataTimeoutRef.current);
      loadTeamDataTimeoutRef.current = null;
    }
    
    // Check if we recently received SignalR updates (unless forced)
    if (!forceRefresh) {
      const timeSinceLastUpdate = Date.now() - lastUpdateTimestampRef.current;
      if (timeSinceLastUpdate < 1000) {  // Reduced from 5 seconds to 1 second
        console.log('Skipping loadTeamData - recent update detected', timeSinceLastUpdate + 'ms ago');
        return;
      }
      
      // Check if we have any recent operations that haven't been reflected yet
      const hasRecentOperations = Array.from(recentOperationsRef.current.values()).some(op => {
        const age = Date.now() - op.timestamp;
        // Different timeout for start (30s) vs stop (10s) operations
        const timeout = op.operation === 'start' ? 30000 : 10000;
        return age < timeout;
      });
      
      if (hasRecentOperations) {
        console.log('Skipping loadTeamData - recent operations still pending');
        // Log which operations are pending
        recentOperationsRef.current.forEach((op, userId) => {
          const age = Date.now() - op.timestamp;
          const timeout = op.operation === 'start' ? 30000 : 10000;
          console.log(`  - User ${userId}: ${op.operation} operation (${age}ms ago, timeout: ${timeout}ms)`);
        });
        return;
      }
    }
    
    // Function to actually load the data
    const doLoadTeamData = async () => {
      try {
        setIsLoading(true);
      
      // Get team members
      let members = await apiService.getTeamMembers(supervisorId);
      
      // Filter by teamId if provided (for multi-team supervisors)
      if (teamId) {
        members = members.filter(m => m.teamId === teamId);
        console.log(`Team members loaded for team ${teamId}:`, members.length);
      } else {
        console.log('Team members loaded:', members.length);
      }
      
      // Get work entries - we need to get entries from the last few days to catch any active ones
      // that might have started before today
      const today = new Date();
      const threeDaysAgo = new Date(today);
      threeDaysAgo.setDate(today.getDate() - 3);
      const startDate = threeDaysAgo.toISOString().split('T')[0];
      
      console.log('Fetching work entries from:', startDate);
      const workEntriesResponse = await apiService.getWorkEntries(startDate);
      const workEntries = Array.isArray(workEntriesResponse) 
        ? workEntriesResponse 
        : (workEntriesResponse.data || []);
      console.log('Work entries loaded:', workEntries.length);
      
      // Also fetch today's entries specifically to ensure we have all recent data
      const todayDate = new Date().toISOString().split('T')[0];
      if (todayDate !== startDate) {
        const todayEntriesResponse = await apiService.getWorkEntries(todayDate);
        const todayEntries = Array.isArray(todayEntriesResponse) 
          ? todayEntriesResponse 
          : (todayEntriesResponse.data || []);
        console.log('Today entries loaded:', todayEntries.length);
        
        // Merge today's entries with existing entries (avoid duplicates)
        todayEntries.forEach(entry => {
          if (!workEntries.find(e => e.id === entry.id)) {
            workEntries.push(entry);
          }
        });
      }
      
      // Also get active work entries for each team member to ensure we don't miss any
      const activeEntryPromises = members.map(async (member) => {
        try {
          // First try the normal active work entry endpoint
          const entry = await apiService.getActiveWorkEntry(member.id, true);
          if (entry && entry.id) {
            console.log(`Active entry found for ${member.name} (${member.id}):`, entry.id);
            return entry;
          }
          
          // If no active entry but user shows as working, search in recent days
          if (member.isWorking) {
            console.log(`User ${member.name} shows as working but no active entry found today. Searching previous days...`);
            
            // Check last 7 days for any unclosed work entries
            for (let daysAgo = 1; daysAgo <= 7; daysAgo++) {
              const date = new Date();
              date.setDate(date.getDate() - daysAgo);
              const dateStr = date.toISOString().split('T')[0];
              
              try {
                const olderEntries = await apiService.getWorkEntries(dateStr, member.id);
                const entries = Array.isArray(olderEntries) ? olderEntries : (olderEntries.data || []);
                const activeEntry = entries.find((e: any) => !e.endTime);
                
                if (activeEntry) {
                  console.log(`Found unclosed work entry for ${member.name} from ${dateStr}:`, activeEntry.id);
                  return activeEntry;
                }
              } catch (err) {
                console.log(`Error checking ${dateStr} for ${member.name}:`, err);
              }
            }
          }
          
          console.log(`No active entry for ${member.name} (${member.id})`);
          return null;
        } catch (error) {
          console.log(`Error getting active entry for ${member.name} (${member.id}):`, error.message);
          return null;
        }
      });
      const activeEntries = await Promise.all(activeEntryPromises);
      console.log('Active entries loaded:', activeEntries.map((entry, idx) => ({
        memberName: members[idx].name,
        memberId: members[idx].id,
        hasEntry: entry !== null,
        entryId: entry?.id,
        endTime: entry?.endTime,
        isActive: entry?.isActive
      })));
      
      // Merge active entries with work entries
      const allEntries = [...workEntries];
      activeEntries.forEach((entry, index) => {
        if (entry && entry.id && !allEntries.find(e => e.id === entry.id)) {
          allEntries.push(entry);
        }
      });

      // Process team members with their status - Using status from User model
      const processedMembers = members.map((member, index) => {
        // Check if this member has a recent operation that hasn't been reflected yet
        const operationKey = getOperationKey(teamId, member.id);
        const recentOp = globalRecentOperations.get(operationKey) || recentOperationsRef.current.get(member.id);
        let isWorking = member.isWorking || false;
        let onBreak = member.onBreak || false;
        let currentWorkEntryId = member.currentWorkEntryId;
        
        if (recentOp) {
          const timeSinceOp = Date.now() - recentOp.timestamp;
          // Different protection windows: 30s for start, 10s for stop operations
          const protectionWindow = recentOp.operation === 'start' ? 30000 : 10000;
          
          // If operation is within protection window, use the local state to prevent confusion
          if (timeSinceOp < protectionWindow) {
            console.log(`Member ${member.name} has recent ${recentOp.operation} operation (${timeSinceOp}ms ago, window: ${protectionWindow}ms), preserving local state`);
            
            if (recentOp.operation === 'start') {
              // Override with local state for recent start - FORCE working status
              isWorking = true;
              onBreak = false;
              // Keep the original currentWorkEntryId or use a temporary one
              if (!currentWorkEntryId && recentOp.projectId && recentOp.locationId) {
                currentWorkEntryId = Date.now() + member.id; // Temporary ID
              }
              console.log(`FORCING member ${member.name} to working status due to recent start operation`);
            } else if (recentOp.operation === 'stop') {
              // Override with local state for recent stop - FORCE inactive status
              isWorking = false;
              onBreak = false;
              currentWorkEntryId = null;
              console.log(`FORCING member ${member.name} to inactive status due to recent stop operation`);
            }
          } else {
            // Operation is older than protection window, remove it and trigger refresh
            console.log(`Removing old ${recentOp.operation} operation for member ${member.name} (${timeSinceOp}ms ago, window: ${protectionWindow}ms) - triggering status refresh`);
            setRecentOperations(prev => {
              const newMap = new Map(prev);
              newMap.delete(member.id);
              return newMap;
            });
            
            // Also remove from global storage
            globalRecentOperations.delete(operationKey);
            
            // Trigger a gentle refresh to get the final status from backend
            // Use a small delay to ensure the recent operation is removed first
            setTimeout(() => {
              console.log(`Auto-refreshing status for member ${member.name} after protection window expired`);
              loadTeamData(true, true); // Force refresh, ignore recent operations
            }, 100);
          }
        }
        
        // Find the current work entry if user is working
        const memberEntries = allEntries.filter(entry => entry.userId === member.id);
        let validActiveEntry = null;
        
        if (isWorking && currentWorkEntryId) {
          // Find the work entry by ID
          validActiveEntry = memberEntries.find(entry => entry.id === currentWorkEntryId);
          
          // If not found in current entries but user is marked as working, check active entries
          if (!validActiveEntry && activeEntries[index]) {
            validActiveEntry = activeEntries[index];
          }
        }
        
        // For compatibility, also check if we have an active entry from the old logic
        if (!validActiveEntry && isWorking) {
          // First try to find an entry without endTime from any date
          validActiveEntry = memberEntries.find(entry => !entry.endTime);
          
          // If still no entry found, check all entries including older dates
          if (!validActiveEntry) {
            // Get all entries for this user, not just today's
            const allUserEntries = allEntries.filter(entry => entry.userId === member.id && !entry.endTime);
            validActiveEntry = allUserEntries[0] || activeEntries[index];
          }
        }
        
        // Enhanced debug logging
        if (memberEntries.length > 0 || member.id === 57 || member.id === 58) {
          console.log(`Member ${member.name} (${member.id}):`, {
            statusFromDB: {
              isWorking: member.isWorking,
              onBreak: member.onBreak,
              currentWorkEntryId: member.currentWorkEntryId,
              lastStatusUpdate: member.lastStatusUpdate
            },
            computedStatus: {
              isWorking,
              onBreak,
              hasActiveEntry: !!validActiveEntry,
              activeEntryId: validActiveEntry?.id
            }
          });
        }
        
        // Calculate today's hours - only count entries from today
        const today = new Date().toISOString().split('T')[0];
        const todayEntries = memberEntries.filter(entry => {
          if (!entry.date) return false;
          const entryDate = typeof entry.date === 'string' 
            ? entry.date.split('T')[0] 
            : new Date(entry.date).toISOString().split('T')[0];
          return entryDate === today;
        });
        
        const todayHours = todayEntries.reduce((total, entry) => {
          if (entry.startTime) {
            // Use the utility function for consistent calculation
            const breakMinutes = (entry as any).totalBreakMinutes || entry.breakMinutes || 0;
            const netHours = calculateWorkDuration(entry.startTime, entry.endTime, breakMinutes);
            
            // Debug logging for hours calculation
            if (member.id === 57 || member.id === 58) {
              console.log(`Hours calc for ${member.name}:`, {
                entryId: entry.id,
                start: entry.startTime,
                end: entry.endTime || 'ongoing',
                breakMinutes,
                netHours: netHours.toFixed(2)
              });
            }
            
            return total + netHours;
          }
          return total;
        }, 0);

        return {
          ...member,
          isWorking: isWorking,
          onBreak: onBreak,
          currentWorkEntry: validActiveEntry,
          activeBreakId: member.currentBreakId,
          todayHours: todayHours
          // isSelected will be set from previous state
        };
      });

      console.log('Processed team members:', processedMembers.map(m => ({
        id: m.id,
        name: m.name,
        isWorking: m.isWorking,
        onBreak: m.onBreak
      })));
      
      // Preserve selection state when updating team members
      setTeamMembers(prevMembers => {
        // Create a map of current selections
        const selectionMap = new Map(prevMembers.map(m => [m.id, m.isSelected || false]));
        
        // Apply selections to processed members
        return processedMembers.map(member => ({
          ...member,
          isSelected: selectionMap.get(member.id) || false
        }));
      });
    } catch (error) {
      console.error('Error loading team data:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele echipei',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
    };
    
    // Execute immediately if forced, otherwise debounce
    if (forceRefresh) {
      doLoadTeamData();
    } else {
      loadTeamDataTimeoutRef.current = setTimeout(doLoadTeamData, 300);
    }
  }, [supervisorId, toast]); // Removed volatile dependencies to prevent re-creation

  // Load initial data
  useEffect(() => {
    loadTeamData();

    // Listen for manual refresh events only
    const handleRefresh = () => {
      console.log('Manual refresh requested');
      // Check if we have recent operations before allowing refresh
      const hasRecentOps = Array.from(recentOperationsRef.current.values()).some(op => {
        const age = Date.now() - op.timestamp;
        // Different timeout for start (30s) vs stop (10s) operations
        const timeout = op.operation === 'start' ? 30000 : 10000;
        return age < timeout;
      });
      
      if (hasRecentOps) {
        console.log('Blocking manual refresh - recent operations in progress');
        // Log which operations are blocking the refresh
        recentOperationsRef.current.forEach((op, userId) => {
          const age = Date.now() - op.timestamp;
          const timeout = op.operation === 'start' ? 30000 : 10000;
          console.log(`  - Blocking refresh due to user ${userId}: ${op.operation} operation (${age}ms ago, timeout: ${timeout}ms)`);
        });
        
        toast({
          title: 'Așteptați',
          description: 'Operațiuni în curs de procesare. Încercați din nou în câteva secunde.',
          duration: 3000
        });
        return;
      }
      
      loadTeamData(true);
    };
    window.addEventListener('supervisor-refresh', handleRefresh);
    
    // Add protection against tab switching triggering refreshes
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('Tab became visible - checking for protected operations before refresh');
        const hasRecentOps = Array.from(recentOperationsRef.current.values()).some(op => {
          const age = Date.now() - op.timestamp;
          const timeout = op.operation === 'start' ? 30000 : 10000;
          return age < timeout;
        });
        
        if (hasRecentOps) {
          console.log('Blocking tab visibility refresh - recent operations in progress');
          recentOperationsRef.current.forEach((op, userId) => {
            const age = Date.now() - op.timestamp;
            const timeout = op.operation === 'start' ? 30000 : 10000;
            console.log(`  - Blocking visibility refresh due to user ${userId}: ${op.operation} operation (${age}ms ago, timeout: ${timeout}ms)`);
          });
          return;
        }
        
        // Only refresh if no recent operations
        setTimeout(() => {
          console.log('Tab visibility refresh after delay');
          loadTeamData(false, false);
        }, 500); // Small delay to avoid rapid fire
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // No auto-refresh - rely on SignalR for real-time updates
    
    return () => {
      window.removeEventListener('supervisor-refresh', handleRefresh);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Clean up any pending timeout
      if (loadTeamDataTimeoutRef.current) {
        clearTimeout(loadTeamDataTimeoutRef.current);
      }
    };
  }, []); // Empty dependencies - only run on mount

  // Subscribe to SignalR events for real-time updates
  useEffect(() => {
    // Check if SignalR functions are available
    if (!realTimeContext) {
      console.warn('RealTime context not available - SignalR updates will not work');
      return;
    }
    
    // Only set up subscriptions if connected
    if (!realTimeContext.state.isConnected) {
      console.warn('SignalR not connected, skipping subscription setup');
      return;
    }
    
    console.log('Setting up SignalR subscriptions for TeamManagementTab');
    console.log('RealTimeContext state:', realTimeContext.state);
    console.log('Is connected:', realTimeContext.state.isConnected);
    console.log('User role:', realTimeContext.state.userRole);
    console.log('Connection ID:', realTimeContext.state.connectionId);
    
    // Handle team member status changes
    const unsubscribeTeamStatus = subscribeToTeamMemberStatusChanged((event) => {
      console.log('SignalR: TeamMemberStatusChanged event received', event);
      
      // Check if we have a recent operation for this user that shouldn't be overridden
      const recentOp = recentOperationsRef.current.get(event.userId);
      if (recentOp) {
        const timeSinceOp = Date.now() - recentOp.timestamp;
        const protectionWindow = recentOp.operation === 'start' ? 30000 : 10000;
        if (timeSinceOp < protectionWindow) {
          console.log(`Ignoring SignalR update for user ${event.userId} - recent ${recentOp.operation} operation in progress (${timeSinceOp}ms ago, window: ${protectionWindow}ms)`);
          return; // Don't update if we have a recent operation
        }
      }
      
      // Update specific team member without full reload
      setTeamMembers(prev => prev.map(member => {
        if (member.id === event.userId) {
          console.log(`Updating member ${member.name} status from SignalR:`, event);
          return {
            ...member,
            isWorking: event.isWorking || false,
            onBreak: event.onBreak || false,
            currentWorkEntry: event.currentWorkEntry,
            activeBreakId: event.activeBreakId
          };
        }
        return member;
      }));
      
      // Track last update to prevent overwriting
      setLastUpdateTimestamp(Date.now());
    });

    // Handle work started events
    const unsubscribeWorkStarted = subscribeToWorkStarted((event) => {
      console.log('SignalR: WorkStarted event received', event);
      
      const memberId = event.userId || event.UserId;
      
      // Check if we should delay processing this event to avoid race conditions
      const operationKey = getOperationKey(teamId, memberId);
      const recentOp = globalRecentOperations.get(operationKey) || recentOperationsRef.current.get(memberId);
      if (recentOp && recentOp.operation === 'start') {
        const timeSinceOp = Date.now() - recentOp.timestamp;
        // Only process SignalR WorkStarted if the start operation is older than 5 seconds
        // This prevents race conditions where SignalR arrives too early
        if (timeSinceOp < 5000) {
          console.log(`Delaying SignalR WorkStarted processing for user ${memberId} - recent start operation (${timeSinceOp}ms ago)`);
          // Set a timeout to process this later
          setTimeout(() => {
            console.log(`Processing delayed WorkStarted event for user ${memberId}`);
            setRecentOperations(prev => {
              const newMap = new Map(prev);
              newMap.delete(memberId);
              return newMap;
            });
            setRecentlyUpdatedMemberIds(prev => new Set([...prev, memberId]));
          }, 5000 - timeSinceOp); // Wait until 5 seconds have passed
          return;
        }
      } else if (recentOp && recentOp.operation === 'stop') {
        const timeSinceOp = Date.now() - recentOp.timestamp;
        if (timeSinceOp < 60000) {
          console.log(`Ignoring SignalR WorkStarted for user ${memberId} - recent stop operation in progress (${timeSinceOp}ms ago)`);
          return; // Don't update if we have a recent stop operation
        }
      }
      
      // Remove from recent operations if it exists (SignalR confirmed the operation)
      setRecentOperations(prev => {
        const newMap = new Map(prev);
        const op = newMap.get(memberId);
        if (op && op.operation === 'start') {
          console.log(`Removing recent start operation for member ${memberId} - confirmed by SignalR`);
          newMap.delete(memberId);
        }
        return newMap;
      });
      
      // Update team member who started work
      setTeamMembers(prev => {
        console.log('Updating team members for WorkStarted, current state:', prev.length);
        console.log('Looking for userId:', event.userId, 'type:', typeof event.userId);
        return prev.map(member => {
          console.log(`Checking member ${member.name} ID: ${member.id} (type: ${typeof member.id}) against event userId: ${event.userId}`);
          if (member.id === event.userId || member.id === Number(event.userId) || String(member.id) === String(event.userId)) {
            console.log(`MATCH! Member ${member.name} (ID: ${member.id}) started work (SignalR)`);
            return {
            ...member,
            isWorking: true,
            onBreak: false,
            currentWorkEntry: {
              id: event.id, // Changed from event.workEntryId to event.id
              userId: event.userId,
              userName: member.name,
              projectId: event.projectId,
              projectName: event.projectName || '',
              locationId: event.locationId,
              locationName: event.locationName || '',
              startTime: event.startTime, // Changed from event.timestamp to event.startTime
              endTime: null,
              breakMinutes: 0,
              // Additional fields that might be needed
              comments: '',
              workDescription: '',
              voiceRecording: ''
            } as any
          };
        }
        return member;
      });
      });
      
      setLastUpdateTimestamp(Date.now());
    });

    // Handle work ended events
    const unsubscribeWorkEnded = subscribeToWorkEnded((event) => {
      console.log('SignalR: WorkEnded event received', event);
      
      const memberId = event.userId || event.UserId;
      
      // Check if we should delay processing this event to avoid race conditions
      const operationKey = getOperationKey(teamId, memberId);
      const recentOp = globalRecentOperations.get(operationKey) || recentOperationsRef.current.get(memberId);
      if (recentOp && recentOp.operation === 'stop') {
        const timeSinceOp = Date.now() - recentOp.timestamp;
        // Only process SignalR WorkEnded if the stop operation is older than 2 seconds
        // This prevents race conditions where SignalR arrives too early
        if (timeSinceOp < 2000) {
          console.log(`Delaying SignalR WorkEnded processing for user ${memberId} - recent stop operation (${timeSinceOp}ms ago)`);
          // Set a timeout to process this later
          setTimeout(() => {
            console.log(`Processing delayed WorkEnded event for user ${memberId}`);
            // Re-check if operation is still protected
            const currentOp = globalRecentOperations.get(operationKey) || recentOperationsRef.current.get(memberId);
            if (!currentOp || currentOp.operation !== 'stop' || Date.now() - currentOp.timestamp > 10000) {
              setRecentOperations(prev => {
                const newMap = new Map(prev);
                newMap.delete(memberId);
                return newMap;
              });
              setRecentlyUpdatedMemberIds(prev => new Set([...prev, memberId]));
            }
          }, 2000 - timeSinceOp); // Wait until 2 seconds have passed
          return;
        }
      } else if (recentOp && recentOp.operation === 'start') {
        const timeSinceOp = Date.now() - recentOp.timestamp;
        if (timeSinceOp < 30000) {
          console.log(`Ignoring SignalR WorkEnded for user ${memberId} - recent start operation in progress (${timeSinceOp}ms ago)`);
          return; // Don't update if we have a recent start operation
        }
      }
      
      // Remove from recent operations if it exists (SignalR confirmed the operation)
      setRecentOperations(prev => {
        const newMap = new Map(prev);
        const op = newMap.get(memberId);
        if (op && op.operation === 'stop') {
          console.log(`Removing recent stop operation for member ${memberId} - confirmed by SignalR`);
          newMap.delete(memberId);
        }
        return newMap;
      });
      
      // Update team member who ended work
      setTeamMembers(prev => {
        console.log('Updating team members for WorkEnded, current state:', prev.length);
        console.log('Looking for userId:', event.userId, 'type:', typeof event.userId);
        return prev.map(member => {
          console.log(`Checking member ${member.name} ID: ${member.id} (type: ${typeof member.id}) against event userId: ${event.userId}`);
          if (member.id === event.userId || member.id === Number(event.userId) || String(member.id) === String(event.userId)) {
            console.log(`MATCH! Member ${member.name} (ID: ${member.id}) ended work (SignalR)`);
          return {
            ...member,
            isWorking: false,
            onBreak: false,
            currentWorkEntry: undefined,
            activeBreakId: undefined
          };
        }
        return member;
      });
      });
      
      setLastUpdateTimestamp(Date.now());
    });

    // Also listen for bulk work status updates if available
    let unsubscribeBulkUpdate = () => {};
    if (realTimeContext && (realTimeContext as any).subscribeToBulkWorkStatusUpdate) {
      unsubscribeBulkUpdate = (realTimeContext as any).subscribeToBulkWorkStatusUpdate((updates: any[]) => {
        console.log('SignalR: Bulk work status update received', updates);
        
        // Update multiple team members at once
        setTeamMembers(prev => prev.map(member => {
          const update = updates.find((u: any) => u.userId === member.id);
          if (update) {
            console.log(`Updating member ${member.name} from bulk update:`, update);
            return {
              ...member,
              isWorking: update.isWorking || false,
              onBreak: false,
              currentWorkEntry: update.isWorking ? member.currentWorkEntry : undefined,
              activeBreakId: undefined
            };
          }
          return member;
        }));
        
        setLastUpdateTimestamp(Date.now());
      });
    }
    
    // Subscribe to user status changes
    const unsubscribeUserEvents = () => {};
    if (subscribeToUserEvents) {
      subscribeToUserEvents((event: any) => {
        console.log('SignalR: UserStatusChanged event received', event);
        
        // Check if event is UserStatusChanged
        if (event.UserId || event.userId) {
          const userId = event.UserId || event.userId;
          
          // Update the team member status from the database status
          setTeamMembers(prev => prev.map(member => {
            if (member.id === userId || member.id === Number(userId)) {
              console.log(`Updating member ${member.name} status from UserStatusChanged:`, {
                isWorking: event.IsWorking ?? event.isWorking,
                onBreak: event.OnBreak ?? event.onBreak,
                currentWorkEntryId: event.CurrentWorkEntryId ?? event.currentWorkEntryId,
                currentBreakId: event.CurrentBreakId ?? event.currentBreakId
              });
              
              return {
                ...member,
                isWorking: event.IsWorking ?? event.isWorking ?? false,
                onBreak: event.OnBreak ?? event.onBreak ?? false,
                currentWorkEntry: member.currentWorkEntry, // Keep existing work entry data
                activeBreakId: event.CurrentBreakId ?? event.currentBreakId
              };
            }
            return member;
          }));
          
          // Remove from recent operations as the status is confirmed
          setRecentOperations(prev => {
            const newMap = new Map(prev);
            if (newMap.has(userId)) {
              console.log(`Removing recent operation for member ${userId} - confirmed by UserStatusChanged`);
              newMap.delete(userId);
            }
            return newMap;
          });
          
          setLastUpdateTimestamp(Date.now());
        }
      });
    }

    return () => {
      unsubscribeTeamStatus();
      unsubscribeWorkStarted();
      unsubscribeWorkEnded();
      unsubscribeBulkUpdate();
      unsubscribeUserEvents();
    };
  }, [realTimeContext, realTimeContext?.state.isConnected, subscribeToTeamMemberStatusChanged, subscribeToWorkStarted, subscribeToWorkEnded, subscribeToUserEvents]);

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    setTeamMembers(members => 
      members.map(m => ({ ...m, isSelected: checked }))
    );
  };

  const handleSelectMember = (memberId: number, checked: boolean) => {
    setTeamMembers(members => 
      members.map(m => 
        m.id === memberId ? { ...m, isSelected: checked } : m
      )
    );
  };

  const getSelectedMembers = () => {
    return teamMembers.filter(m => m.isSelected);
  };

  const handleStartWork = async () => {
    if (!selectedProject || !selectedLocation) {
      toast({
        title: 'Atenție',
        description: 'Selectați un proiect și o locație',
        variant: 'destructive'
      });
      return;
    }

    const selectedMembers = getSelectedMembers();
    if (selectedMembers.length === 0) {
      toast({
        title: 'Atenție',
        description: 'Selectați cel puțin un membru',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    
    // Filter only members who are not working
    const membersToStart = selectedMembers.filter(m => !m.isWorking);
    
    if (membersToStart.length === 0) {
      toast({
        title: 'Atenție',
        description: 'Toți membrii selectați lucrează deja',
        variant: 'destructive'
      });
      setIsLoading(false);
      return;
    }

    console.log('Starting bulk work for members:', {
      memberIds: membersToStart.map(m => m.id),
      memberNames: membersToStart.map(m => m.name),
      projectId: parseInt(selectedProject),
      locationId: parseInt(selectedLocation)
    });
    
    // Initialize progress tracking
    setBulkOperationProgress({
      isProcessing: true,
      currentMember: null,
      processedCount: 0,
      totalCount: membersToStart.length,
      results: []
    });

    try {
      // Mark members as being processed
      const memberIds = membersToStart.map(m => m.id);
      setProcessingMemberIds(new Set(memberIds));
      
      // Show which members are being processed
      const memberNames = membersToStart.map(m => m.name).join(', ');
      setBulkOperationProgress(prev => ({
        ...prev!,
        currentMember: `Se pornește munca pentru: ${memberNames}`
      }));
      
      // Call the API
      let result;
      try {
        result = await apiService.bulkStartWork(
          memberIds,
          parseInt(selectedProject),
          parseInt(selectedLocation)
        );
        console.log('Bulk start work API response:', result);
      } catch (apiError) {
        console.error('API call failed:', apiError);
        throw new Error(apiError instanceof Error ? apiError.message : 'Conexiunea cu serverul a eșuat');
      }

      // Process the results
      const results = (result?.results || []);
      
      if (!Array.isArray(results)) {
        console.error('Invalid API response - results is not an array:', results);
        throw new Error('Răspuns invalid de la server');
      }
      
      // Track successfully started members
      const successfulMemberIds = new Set<number>();
      results.forEach((r: any) => {
        if (r.success && r.userId) {
          successfulMemberIds.add(r.userId);
          // Track this operation
          setRecentOperations(prev => {
            const newMap = new Map(prev);
            const now = Date.now();
            
            // Clean up old operations (older than 90 seconds)
            for (const [userId, op] of newMap.entries()) {
              if (now - op.timestamp > 90000) {
                newMap.delete(userId);
              }
            }
            
            newMap.set(r.userId, {
              operation: 'start',
              timestamp: now,
              projectId: parseInt(selectedProject),
              locationId: parseInt(selectedLocation)
            });
            
            // Schedule automatic refresh after protection window expires
            setTimeout(() => {
              console.log(`Auto-refresh scheduled for start operation on user ${r.userId}`);
              setRecentOperations(prev => {
                const updatedMap = new Map(prev);
                if (updatedMap.has(r.userId)) {
                  console.log(`Removing expired start operation for user ${r.userId} and triggering refresh`);
                  updatedMap.delete(r.userId);
                  // Trigger refresh after removing the operation
                  setTimeout(() => loadTeamData(true, true), 100);
                }
                return updatedMap;
              });
            }, 32000); // 32 seconds (slightly after the 30s protection window)
            
            return newMap;
          });
        } else {
          console.error(`Failed to start work for user ${r.userId}:`, r.error);
        }
      });
      
      console.log('Successfully started members:', Array.from(successfulMemberIds));
      
      // Update progress
      setBulkOperationProgress(prev => ({
        ...prev!,
        isProcessing: false,
        processedCount: membersToStart.length,
        currentMember: 'Procesare finalizată',
        results: (results || []).map((r: any) => {
          const member = membersToStart.find(m => m.id === r.userId);
          return {
            memberId: r.userId,
            memberName: member?.name || 'Unknown',
            success: r.success,
            error: r.error
          };
        })
      }));

      // Show success/error toasts
      if (result.successCount > 0) {
        toast({
          title: 'Succes',
          description: `Munca a început pentru ${result.successCount} din ${membersToStart.length} membri`,
          duration: 5000,
        });
      }
      
      if (result.errorCount > 0) {
        toast({
          title: 'Atenție',
          description: `${result.errorCount} membri nu au putut începe munca. Vezi detaliile mai jos.`,
          variant: 'destructive',
          duration: 5000,
        });
      }
      
      // IMPORTANT: Immediately update the local state to reflect started members
      // This ensures UI responsiveness while waiting for backend/SignalR updates
      const projectData = projects.find(p => p.id === parseInt(selectedProject));
      const locationData = locations.find(l => l.id === parseInt(selectedLocation));
      
      // First, update the isWorking status immediately
      // Also handle failed operations where member is already working
      setTeamMembers(prevMembers => 
        prevMembers.map(m => {
          if (successfulMemberIds.has(m.id)) {
            console.log(`Immediately updating UI for started member ${m.name} (ID: ${m.id})`);
            // Get the work entry ID from the results if available
            const resultForMember = results.find((r: any) => r.userId === m.id);
            const workEntryId = resultForMember?.workEntryId;
            
            return {
              ...m,
              isWorking: true, // This is the key field for status display
              onBreak: false,
              currentWorkEntryId: workEntryId || Date.now() + m.id, // Use real ID if available
              currentWorkEntry: {
                id: workEntryId || Date.now() + m.id,
                userId: m.id,
                userName: m.name,
                projectId: parseInt(selectedProject),
                projectName: projectData?.name || '',
                locationId: parseInt(selectedLocation),
                locationName: locationData?.name || '',
                startTime: new Date().toISOString(),
                endTime: null,
                breakMinutes: 0,
                comments: '',
                workDescription: '',
                voiceRecording: ''
              } as any,
              isSelected: false // Clear selection
            };
          }
          
          // Check if this member failed due to already working
          const failedResult = results.find((r: any) => r.userId === m.id && !r.success);
          if (failedResult && (failedResult.error?.includes('already') || 
                              failedResult.error?.includes('deja') ||
                              failedResult.error?.includes('lucrează'))) {
            console.log(`Member ${m.name} is already working according to backend, updating to active state`);
            return {
              ...m,
              isWorking: true, // Set to true since backend says already working
              onBreak: false,
              // Keep existing work entry if present, otherwise clear it
              // The next refresh will get the correct work entry from backend
              isSelected: false
            };
          }
          
          return {
            ...m,
            isSelected: false // Clear selection for non-started members too
          };
        })
      );
      
      // Mark these members as recently updated for visual feedback
      setRecentlyUpdatedMemberIds(successfulMemberIds);
      
      // Clear processing state only after a delay to prevent UI flicker
      setTimeout(() => {
        setProcessingMemberIds(new Set());
      }, 5000); // 5 seconds delay
      
      // Clear selections
      handleSelectAll(false);
      
      // Set timestamp to prevent immediate refresh from overwriting our changes
      setLastUpdateTimestamp(Date.now());
      
      // Keep visual indicators for 3 seconds
      setTimeout(() => {
        setRecentlyUpdatedMemberIds(new Set());
      }, 3000);
      
      // Keep progress visible for 10 seconds
      setTimeout(() => {
        setBulkOperationProgress(null);
      }, 10000);
      
      // Schedule a delayed refresh to sync with backend state
      // This is a backup in case SignalR events don't arrive
      setTimeout(async () => {
        console.log('Performing delayed refresh to sync with backend...');
        
        // Fetch fresh user data to ensure we have the updated isWorking status
        try {
          const freshMembers = await apiService.getTeamMembers(supervisorId);
          console.log('Fresh team members after bulk operation:', freshMembers);
          
          // Also fetch active work entries for the started members
          const activeEntryPromises = Array.from(successfulMemberIds).map(userId =>
            apiService.getActiveWorkEntry(userId).catch(err => {
              console.log(`Failed to get active entry for user ${userId}:`, err);
              return null;
            })
          );
          const activeEntries = await Promise.all(activeEntryPromises);
          const activeEntriesMap = new Map(
            activeEntries
              .filter(e => e !== null)
              .map(e => [e!.userId, e])
          );
          
          // Update the team members with fresh data from backend
          setTeamMembers(prevMembers => 
            prevMembers.map(m => {
              const freshMember = freshMembers.find(fm => fm.id === m.id);
              const activeEntry = activeEntriesMap.get(m.id);
              
              if (freshMember) {
                console.log(`Updated member ${m.name} with fresh data:`, {
                  isWorking: freshMember.isWorking,
                  onBreak: freshMember.onBreak,
                  currentWorkEntryId: freshMember.currentWorkEntryId,
                  hasActiveEntry: !!activeEntry
                });
                
                return {
                  ...m,
                  isWorking: freshMember.isWorking, // Use fresh isWorking status
                  onBreak: freshMember.onBreak,
                  currentWorkEntryId: freshMember.currentWorkEntryId,
                  currentBreakId: freshMember.currentBreakId,
                  lastStatusUpdate: freshMember.lastStatusUpdate,
                  currentWorkEntry: activeEntry || m.currentWorkEntry // Use fetched entry or keep existing
                };
              }
              return m;
            })
          );
        } catch (error) {
          console.error('Error fetching fresh team data:', error);
          // If error, do a full reload but respect recent operations
          await loadTeamData(true, true);
        }
      }, 2000); // 2 seconds delay - backend is now optimized for fast status updates
      
    } catch (error) {
      console.error('Error starting work:', error);
      
      // Show error in progress
      setBulkOperationProgress(prev => ({
        isProcessing: false,
        currentMember: `Eroare: ${error instanceof Error ? error.message : 'Eroare necunoscută'}`,
        processedCount: prev?.processedCount || 0,
        totalCount: prev?.totalCount || membersToStart.length,
        results: Array.isArray(prev?.results) ? prev.results : []
      }));
      
      // Clear processing state on error
      setProcessingMemberIds(new Set());
      
      // Revert UI changes by reloading
      await loadTeamData(true);
      
      toast({
        title: 'Eroare',
        description: error instanceof Error ? error.message : 'A apărut o eroare la pornirea muncii',
        variant: 'destructive'
      });
      
      // Hide progress after delay
      setTimeout(() => {
        setBulkOperationProgress(null);
        handleSelectAll(false);
      }, 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStopWork = () => {
    const selectedMembers = getSelectedMembers().filter(m => m.isWorking);
    if (selectedMembers.length === 0) {
      toast({
        title: 'Atenție',
        description: 'Selectați membri care lucrează',
        variant: 'destructive'
      });
      return;
    }
    
    // Check if all selected members have valid work entries
    const membersWithInvalidEntries = selectedMembers.filter(m => !m.currentWorkEntry || !m.currentWorkEntry.id);
    if (membersWithInvalidEntries.length > 0) {
      console.error('Members with invalid work entries:', membersWithInvalidEntries.map(m => ({
        name: m.name,
        id: m.id,
        currentWorkEntry: m.currentWorkEntry
      })));
      toast({
        title: 'Eroare',
        description: 'Unii membri selectați nu au sesiuni de lucru valide. Vă rugăm să reîncărcați pagina.',
        variant: 'destructive'
      });
      // Force reload team data
      loadTeamData(true);
      return;
    }

    setSelectedForStop(selectedMembers);
    setShowStopDialog(true);
  };

  const confirmStopWork = async () => {
    setIsLoading(true);
    
    const workEntryIds = selectedForStop
      .filter(m => m.currentWorkEntry && m.currentWorkEntry.id)
      .map(m => m.currentWorkEntry!.id);
    
    console.log('Work entry IDs to stop:', workEntryIds);
    
    if (workEntryIds.length === 0) {
      toast({
        title: 'Eroare',
        description: 'Nu există sesiuni de lucru valide de oprit',
        variant: 'destructive'
      });
      setIsLoading(false);
      setShowStopDialog(false);
      return;
    }

    // Initialize progress tracking
    setBulkOperationProgress({
      isProcessing: true,
      currentMember: null,
      processedCount: 0,
      totalCount: selectedForStop.length,
      results: []
    });

    try {
      // Mark members as being processed
      const memberIds = selectedForStop.map(m => m.id);
      setProcessingMemberIds(new Set(memberIds));
      
      // Show which members are being processed
      const memberNames = selectedForStop.map(m => m.name).join(', ');
      setBulkOperationProgress(prev => ({
        ...prev!,
        currentMember: `Se oprește munca pentru: ${memberNames}`
      }));
      
      // Create a mapping of workEntryId to member for quick lookup
      const workEntryToMember = new Map(
        selectedForStop.map(m => [m.currentWorkEntry?.id, m])
      );
      
      // Call the API
      let result;
      try {
        result = await apiService.bulkStopWork(workEntryIds, stopComments);
        console.log('Bulk stop work API response:', result);
      } catch (apiError) {
        console.error('API call failed:', apiError);
        throw new Error(apiError instanceof Error ? apiError.message : 'Conexiunea cu serverul a eșuat');
      }
      
      // Process the results
      const results = (result?.results || []);
      
      if (!Array.isArray(results)) {
        console.error('Invalid API response - results is not an array:', results);
        throw new Error('Răspuns invalid de la server');
      }
      
      // Track successfully stopped members
      const successfulMemberIds = new Set<number>();
      results.forEach((r: any) => {
        if (r.success) {
          let userId: number | undefined;
          if (r.workEntryId) {
            const member = workEntryToMember.get(r.workEntryId);
            if (member) {
              userId = member.id;
              successfulMemberIds.add(member.id);
            }
          } else if (r.userId) {
            userId = r.userId;
            successfulMemberIds.add(r.userId);
          }
          
          // Track this operation
          if (userId) {
            setRecentOperations(prev => {
              const newMap = new Map(prev);
              const now = Date.now();
              
              // Clean up old operations (older than 90 seconds)
              for (const [userId, op] of newMap.entries()) {
                if (now - op.timestamp > 90000) {
                  newMap.delete(userId);
                }
              }
              
              newMap.set(userId, {
                operation: 'stop',
                timestamp: now
              });
              
              // Schedule automatic refresh after protection window expires
              setTimeout(() => {
                console.log(`Auto-refresh scheduled for stop operation on user ${userId}`);
                setRecentOperations(prev => {
                  const updatedMap = new Map(prev);
                  if (updatedMap.has(userId)) {
                    console.log(`Removing expired stop operation for user ${userId} and triggering refresh`);
                    updatedMap.delete(userId);
                    // Trigger refresh after removing the operation
                    setTimeout(() => loadTeamData(true, true), 100);
                  }
                  return updatedMap;
                });
              }, 12000); // 12 seconds (slightly after the 10s protection window)
              
              return newMap;
            });
          }
        } else {
          console.error(`Failed to stop work:`, r);
        }
      });
      
      console.log('Successfully stopped members:', Array.from(successfulMemberIds));
      
      // Update progress
      setBulkOperationProgress(prev => ({
        ...prev!,
        isProcessing: false,
        processedCount: selectedForStop.length,
        currentMember: 'Procesare finalizată',
        results: (results || []).map((r: any) => {
          const member = r.workEntryId ? workEntryToMember.get(r.workEntryId) : 
                        selectedForStop.find(m => m.id === r.userId);
          return {
            memberId: member?.id || r.userId || 0,
            memberName: member?.name || 'Unknown',
            success: r.success,
            error: r.error
          };
        })
      }));
      
      // Show success/error toasts
      if (result.successCount > 0) {
        toast({
          title: 'Succes',
          description: `Munca s-a oprit pentru ${result.successCount} din ${selectedForStop.length} membri`,
          duration: 5000,
        });
      }
      
      if (result.errorCount > 0) {
        toast({
          title: 'Atenție',
          description: `${result.errorCount} membri nu au putut opri munca. Vezi detaliile mai jos.`,
          variant: 'destructive',
          duration: 5000,
        });
      }
      
      // Close the dialog
      setShowStopDialog(false);
      setStopComments('');
      
      // IMPORTANT: Immediately update the local state to reflect stopped members
      // This ensures UI responsiveness while waiting for backend/SignalR updates
      // Also handle failed operations where member has no active session
      setTeamMembers(prevMembers => 
        prevMembers.map(m => {
          if (successfulMemberIds.has(m.id)) {
            console.log(`Immediately updating UI for stopped member ${m.name} (ID: ${m.id})`);
            return {
              ...m,
              isWorking: false, // This is the key field for status display
              onBreak: false,
              currentWorkEntryId: null,
              currentBreakId: null,
              currentWorkEntry: undefined,
              activeBreakId: undefined,
              isSelected: false // Clear selection
            };
          }
          
          // Check if this member failed due to no active session
          const failedResult = results.find((r: any) => {
            const member = r.workEntryId ? workEntryToMember.get(r.workEntryId) : 
                          selectedForStop.find(sm => sm.id === r.userId);
            return member?.id === m.id && !r.success;
          });
          
          if (failedResult && (failedResult.error?.includes('no active') || 
                              failedResult.error?.includes('nu are') ||
                              failedResult.error?.includes('not found') ||
                              failedResult.error?.includes('negăsit'))) {
            console.log(`Member ${m.name} has no active session, updating to inactive state`);
            return {
              ...m,
              isWorking: false, // Set to false since backend says no active session
              onBreak: false,
              currentWorkEntryId: null,
              currentBreakId: null,
              currentWorkEntry: undefined,
              activeBreakId: undefined,
              isSelected: false
            };
          }
          
          return {
            ...m,
            isSelected: false // Clear selection for other members
          };
        })
      );
      
      // Mark these members as recently updated for visual feedback
      setRecentlyUpdatedMemberIds(successfulMemberIds);
      
      // Clear processing state only after a delay to prevent UI flicker
      setTimeout(() => {
        setProcessingMemberIds(new Set());
      }, 5000); // 5 seconds delay
      
      // Clear selections
      handleSelectAll(false);
      
      // Set timestamp to prevent immediate refresh from overwriting our changes
      setLastUpdateTimestamp(Date.now());
      
      // Keep visual indicators for 3 seconds
      setTimeout(() => {
        setRecentlyUpdatedMemberIds(new Set());
      }, 3000);
      
      // Keep progress visible for 10 seconds
      setTimeout(() => {
        setBulkOperationProgress(null);
      }, 10000);
      
      // Schedule a delayed refresh to sync with backend state
      // This is a backup in case SignalR events don't arrive
      setTimeout(async () => {
        console.log('Performing delayed refresh to sync with backend...');
        
        // Fetch fresh user data to ensure we have the updated isWorking status
        try {
          const freshMembers = await apiService.getTeamMembers(supervisorId);
          console.log('Fresh team members after bulk stop operation:', freshMembers);
          
          // Update the team members with fresh data from backend
          setTeamMembers(prevMembers => 
            prevMembers.map(m => {
              const freshMember = freshMembers.find(fm => fm.id === m.id);
              if (freshMember) {
                console.log(`Updated member ${m.name} with fresh data after stop:`, {
                  isWorking: freshMember.isWorking,
                  onBreak: freshMember.onBreak,
                  currentWorkEntryId: freshMember.currentWorkEntryId
                });
                
                // For stopped members, clear work entry data
                if (!freshMember.isWorking) {
                  return {
                    ...m,
                    isWorking: false,
                    onBreak: false,
                    currentWorkEntryId: null,
                    currentBreakId: null,
                    lastStatusUpdate: freshMember.lastStatusUpdate,
                    currentWorkEntry: undefined
                  };
                }
                
                return {
                  ...m,
                  isWorking: freshMember.isWorking,
                  onBreak: freshMember.onBreak,
                  currentWorkEntryId: freshMember.currentWorkEntryId,
                  currentBreakId: freshMember.currentBreakId,
                  lastStatusUpdate: freshMember.lastStatusUpdate
                };
              }
              return m;
            })
          );
        } catch (error) {
          console.error('Error fetching fresh team data:', error);
          // If error, do a full reload but respect recent operations
          await loadTeamData(true, true);
        }
      }, 500); // 500ms delay for stop operations - quick sync with backend
      
    } catch (error) {
      console.error('Error stopping work:', error);
      
      // Show error in progress
      setBulkOperationProgress(prev => ({
        isProcessing: false,
        currentMember: `Eroare: ${error instanceof Error ? error.message : 'Eroare necunoscută'}`,
        processedCount: prev?.processedCount || 0,
        totalCount: prev?.totalCount || selectedForStop.length,
        results: Array.isArray(prev?.results) ? prev.results : []
      }));
      
      // Clear processing state on error
      setProcessingMemberIds(new Set());
      
      // Revert UI changes by reloading
      await loadTeamData(true);
      
      toast({
        title: 'Eroare',
        description: error instanceof Error ? error.message : 'A apărut o eroare la oprirea muncii',
        variant: 'destructive'
      });
      
      // Hide progress and close dialog after delay
      setTimeout(() => {
        setBulkOperationProgress(null);
        setShowStopDialog(false);
        setStopComments('');
        handleSelectAll(false);
      }, 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMaterial = (member: TeamMember) => {
    console.log('handleAddMaterial called for member:', {
      id: member.id,
      name: member.name,
      currentWorkEntry: member.currentWorkEntry
    });
    
    if (!member.currentWorkEntry) {
      toast({
        title: 'Atenție',
        description: 'Membrul nu are o sesiune activă de lucru',
        variant: 'destructive'
      });
      return;
    }
    
    // Check if the work entry is still active
    if (member.currentWorkEntry.endTime) {
      toast({
        title: 'Atenție',
        description: 'Nu se pot adăuga materiale la o sesiune încheiată. Membrul trebuie să înceapă o nouă sesiune de lucru.',
        variant: 'destructive'
      });
      return;
    }
    
    // Validate work entry and user IDs
    if (!member.currentWorkEntry.id || !member.id) {
      console.error('Invalid IDs:', {
        workEntryId: member.currentWorkEntry.id,
        userId: member.id
      });
      toast({
        title: 'Eroare',
        description: 'Date invalide. Vă rugăm să reîncărcați pagina.',
        variant: 'destructive'
      });
      return;
    }
    
    setSelectedForMaterial(member);
    setShowMaterialDialog(true);
  };

  const handleSyncUserStatus = async () => {
    setIsSyncing(true);
    try {
      const result = await apiService.syncUserStatus();
      toast({
        title: 'Status sincronizat',
        description: `${result.correctedCount} utilizatori au fost corectați`,
        variant: 'default'
      });
      // Reload data to show updated status
      loadTeamData(true);
    } catch (error: any) {
      console.error('Sync error:', error);
      toast({
        title: 'Eroare la sincronizare',
        description: error.message || 'Nu s-a putut sincroniza statusul utilizatorilor',
        variant: 'destructive'
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const getStatusBadge = (member: TeamMember) => {
    const isProcessing = processingMemberIds.has(member.id);
    const isRecentlyUpdated = recentlyUpdatedMemberIds.has(member.id);
    
    // Check both local and global operations
    const operationKey = getOperationKey(teamId, member.id);
    const recentOp = globalRecentOperations.get(operationKey) || recentOperations.get(member.id);
    const hasRecentOperation = !!recentOp;
    
    // Check if member appears to be working but missing location/project data
    const isWorkingButIncomplete = member.isWorking && member.currentWorkEntry && (
      !member.currentWorkEntry.locationName || 
      !member.currentWorkEntry.projectName ||
      member.currentWorkEntry.locationName === '' ||
      member.currentWorkEntry.projectName === ''
    );
    
    // Check for status sync issues (user shows as working but no work entry)
    const hasSyncIssue = member.isWorking && !member.currentWorkEntry;
    
    if (isProcessing) {
      return <Badge variant="warning" className="animate-pulse">Se procesează...</Badge>;
    }
    
    // Show processing state for recent operations that haven't completed yet
    if (hasRecentOperation) {
      const timeSinceOp = Date.now() - (recentOp?.timestamp || 0);
      const processingWindow = recentOp?.operation === 'start' ? 30000 : 10000;
      
      // Show processing for different times based on operation type
      if (timeSinceOp < processingWindow) {
        const isStartOperation = recentOp?.operation === 'start';
        
        // For stop operations, show processing regardless of current working status
        // For start operations, show processing if not recently updated or if incomplete
        if (!isStartOperation || !isRecentlyUpdated || isWorkingButIncomplete) {
          return (
            <Badge variant="warning" className="animate-pulse">
              {isStartOperation ? 'Se procesează locația și statusul...' : 'Se procesează oprirea...'}
            </Badge>
          );
        }
      }
    }
    
    // Show processing state if working but missing location/project data
    if (isWorkingButIncomplete) {
      return (
        <Badge variant="warning" className="animate-pulse">
          Se procesează locația și statusul...
        </Badge>
      );
    }
    
    // Show sync issue if user is marked as working but has no work entry
    if (hasSyncIssue) {
      return (
        <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-300">
          Status desincronizat
        </Badge>
      );
    }
    
    if (!member.isWorking) {
      return (
        <Badge variant="secondary" className={isRecentlyUpdated ? 'ring-2 ring-green-400' : ''}>
          Inactiv
        </Badge>
      );
    }
    if (member.onBreak) {
      return (
        <Badge variant="warning" className={isRecentlyUpdated ? 'ring-2 ring-green-400' : ''}>
          În pauză
        </Badge>
      );
    }
    return (
      <Badge variant="success" className={isRecentlyUpdated ? 'ring-2 ring-green-400' : ''}>
        La lucru
      </Badge>
    );
  };

  const workingCount = teamMembers.filter(m => m.isWorking).length;
  const onBreakCount = teamMembers.filter(m => m.onBreak).length;

  return (
    <div className="space-y-6">
      {/* Bulk Operation Progress */}
      {bulkOperationProgress && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  {bulkOperationProgress.isProcessing ? 'Procesare în curs...' : 'Procesare finalizată'}
                </h3>
                <span className="text-sm text-gray-600">
                  {bulkOperationProgress.processedCount} / {bulkOperationProgress.totalCount} membri procesați
                </span>
              </div>
              
              {bulkOperationProgress.currentMember && (
                <div className="flex items-center gap-2">
                  <Loader2 className={`h-4 w-4 ${bulkOperationProgress.isProcessing ? 'animate-spin' : ''}`} />
                  <p className="text-sm">{bulkOperationProgress.currentMember}</p>
                </div>
              )}
              
              {bulkOperationProgress.results && bulkOperationProgress.results.length > 0 && (
                <div className="space-y-2 mt-4">
                  <h4 className="font-medium text-sm">Rezultate:</h4>
                  {(bulkOperationProgress.results || []).map((result, index) => (
                    <div key={index} className={`flex items-center justify-between p-2 rounded ${
                      result.success ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      <span className="text-sm font-medium">{result.memberName}</span>
                      <span className={`text-sm ${result.success ? 'text-green-700' : 'text-red-700'}`}>
                        {result.success ? '✓ Succes' : `✗ ${result.error || 'Eroare'}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Echipă</p>
                <p className="text-2xl font-bold">{teamMembers.length}</p>
              </div>
              <Users className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">La Lucru</p>
                <p className="text-2xl font-bold text-green-600">{workingCount}</p>
              </div>
              <Play className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">În Pauză</p>
                <p className="text-2xl font-bold text-yellow-600">{onBreakCount}</p>
              </div>
              <Coffee className="h-8 w-8 text-yellow-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5" />
            Acțiuni în Masă
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Proiect</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger>
                  <SelectValue placeholder="Selectează proiect" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={project.id.toString()}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Locație</Label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger>
                  <SelectValue placeholder="Selectează locație" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(location => (
                    <SelectItem key={location.id} value={location.id.toString()}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleStartWork}
              disabled={isLoading || (!selectedProject || !selectedLocation) || bulkOperationProgress?.isProcessing}
              className="flex items-center gap-2"
            >
              {isLoading && bulkOperationProgress?.isProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              Începe Lucru Selectați
            </Button>

            <Button
              onClick={handleStopWork}
              disabled={isLoading || bulkOperationProgress?.isProcessing}
              variant="destructive"
              className="flex items-center gap-2"
            >
              {isLoading && bulkOperationProgress?.isProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Square className="h-4 w-4" />
              )}
              Oprește Lucru Selectați
            </Button>
            
            {/* Show sync button if there are status sync issues */}
            {teamMembers.some(m => m.isWorking && !m.currentWorkEntry) && (
              <Button
                onClick={handleSyncUserStatus}
                disabled={isSyncing || bulkOperationProgress?.isProcessing}
                variant="outline"
                className="flex items-center gap-2 border-orange-500 text-orange-600 hover:bg-orange-50"
              >
                {isSyncing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Sincronizează Status
              </Button>
            )}
          </div>

          {(!selectedProject || !selectedLocation) && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Selectați un proiect și o locație pentru a putea începe munca
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Team Members List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Membri Echipă {teamName ? `- ${teamName}` : ''}
            </span>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectAll}
                onCheckedChange={handleSelectAll}
              />
              <Label className="text-sm font-normal">Selectează toți</Label>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {teamMembers.map(member => {
              const isProcessing = processingMemberIds.has(member.id);
              const isRecentlyUpdated = recentlyUpdatedMemberIds.has(member.id);
              
              return (
                <div
                  key={member.id}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-all duration-300 ${
                    isProcessing ? 'bg-yellow-50 border-yellow-400 animate-pulse' :
                    isRecentlyUpdated ? 'bg-green-50 border-green-400' :
                    member.isSelected ? 'bg-blue-50 border-blue-300' : 
                    'bg-white'
                  }`}
                >
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={member.isSelected || false}
                    onCheckedChange={(checked) => handleSelectMember(member.id, checked as boolean)}
                  />
                  <div>
                    <p className="font-medium">{member.name}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      {member.currentWorkEntry && (
                        <>
                          <Briefcase className="h-3 w-3" />
                          <span>{member.currentWorkEntry.projectName}</span>
                          <span>•</span>
                          <MapPin className="h-3 w-3" />
                          <span>{member.currentWorkEntry.locationName}</span>
                          {/* Show warning if work is from previous day */}
                          {member.currentWorkEntry.startTime && (() => {
                            const startDate = new Date(member.currentWorkEntry.startTime).toDateString();
                            const today = new Date().toDateString();
                            if (startDate !== today) {
                              return (
                                <>
                                  <span>•</span>
                                  <AlertTriangle className="h-3 w-3 text-orange-500" />
                                  <span className="text-orange-500 text-xs font-medium">
                                    Din {new Date(member.currentWorkEntry.startTime).toLocaleDateString('ro-RO')}
                                  </span>
                                </>
                              );
                            }
                            return null;
                          })()}
                        </>
                      )}
                      <span>•</span>
                      <Clock className="h-3 w-3" />
                      <span>{formatDuration(member.todayHours)} azi</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {getStatusBadge(member)}
                  
                  {member.isWorking && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAddMaterial(member)}
                      className="flex items-center gap-1"
                    >
                      <Package className="h-3 w-3" />
                      Material
                    </Button>
                  )}
                </div>
              </div>
              );
            })}

            {teamMembers.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                Nu există membri în echipă
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stop Work Dialog */}
      <Dialog open={showStopDialog} onOpenChange={setShowStopDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Oprește Munca</DialogTitle>
            <DialogDescription>
              Opriți munca pentru {selectedForStop.length} membri selectați
            </DialogDescription>
          </DialogHeader>
          
          {/* Show warning for stale entries */}
          {selectedForStop.some(m => {
            if (!m.currentWorkEntry?.startTime) return false;
            const startDate = new Date(m.currentWorkEntry.startTime).toDateString();
            const today = new Date().toDateString();
            return startDate !== today;
          }) && (
            <Alert className="border-orange-500 bg-orange-50">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              <AlertDescription className="text-orange-700">
                <strong>Atenție:</strong> Unii membri au sesiuni de lucru începute în zile anterioare:
                <ul className="mt-2 list-disc list-inside">
                  {selectedForStop.filter(m => {
                    if (!m.currentWorkEntry?.startTime) return false;
                    const startDate = new Date(m.currentWorkEntry.startTime).toDateString();
                    const today = new Date().toDateString();
                    return startDate !== today;
                  }).map(m => (
                    <li key={m.id}>
                      {m.name} - început la {new Date(m.currentWorkEntry!.startTime).toLocaleString('ro-RO')}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-4">
            <div>
              <Label>Comentarii (opțional)</Label>
              <Textarea
                value={stopComments}
                onChange={(e) => setStopComments(e.target.value)}
                placeholder="Adăugați comentarii despre munca efectuată..."
                rows={3}
              />
            </div>

            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Această acțiune va opri munca pentru toți membrii selectați.
                Asigurați-vă că au finalizat activitățile.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowStopDialog(false)}
            >
              Anulează
            </Button>
            <Button
              onClick={confirmStopWork}
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Confirmă Oprirea
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Material Dialog */}
      {showMaterialDialog && selectedForMaterial && (
        <Dialog open={showMaterialDialog} onOpenChange={setShowMaterialDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Adaugă Consum Material - {selectedForMaterial.name}</DialogTitle>
            </DialogHeader>
            
            <MaterialConsumptionForm
              workEntryId={selectedForMaterial.currentWorkEntry!.id}
              addedByUserId={supervisorUser.id}
              addedForUserId={selectedForMaterial.id}
              onSuccess={() => {
                setShowMaterialDialog(false);
                toast({
                  title: 'Succes',
                  description: 'Material adăugat cu succes',
                });
              }}
              onCancel={() => setShowMaterialDialog(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};